import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { common, createLowlight } from 'lowlight';
import { AVATAR_MENTION_COLOR_MAP } from '@/lib/colorsMap';

// Language imports
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import python from 'highlight.js/lib/languages/python';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import html from 'highlight.js/lib/languages/xml';

// Create shared lowlight instance
export const lowlight = createLowlight({
    ...common,
    javascript,
    typescript,
    python,
    html,
    css,
    json,
    sql,
    bash
});

// Shared editor classes
export const baseEditorClasses = 'prose prose-sm dark:prose-invert focus:outline-none max-w-none py-4 \
[&>*]:mb-3.5 \
[&>ul]:list-disc [&>ol]:list-decimal [&>ul]:ml-4 [&>ol]:ml-4 \
[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-4 [&_ol]:ml-4 \
[&>h1]:text-2xl [&>h2]:text-xl [&>h3]:text-lg \
[&>h1]:font-bold [&>h2]:font-bold [&>h3]:font-bold \
[&>h1]:mt-6 [&>h2]:mt-5 [&>h3]:mt-4 \
[&>blockquote]:border-l-4 [&>blockquote]:border-neutral-300 [&>blockquote]:pl-4 [&>blockquote]:italic \
[&_.ProseMirror-gapcursor]:relative [&_.ProseMirror-gapcursor]:after:absolute [&_.ProseMirror-gapcursor]:after:left-0 [&_.ProseMirror-gapcursor]:after:w-[1px] [&_.ProseMirror-gapcursor]:after:h-5 [&_.ProseMirror-gapcursor]:after:bg-primary';

export const codeEditorClasses = [
    '[&>pre]:bg-neutral-200/80 dark:[&>pre]:bg-muted/40',
    '[&>pre]:p-4',
    '[&>pre]:rounded-lg',
    '[&>pre]:my-4',
    '[&>pre]:overflow-x-auto',
    '[&>:not(pre)>code]:bg-muted',
    '[&>:not(pre)>code]:text-primary',
    '[&>:not(pre)>code]:rounded',
    '[&>:not(pre)>code]:px-1.5',
    '[&>:not(pre)>code]:py-0.5',
].join(' ');

export const commentEditorClasses = 'prose prose-sm dark:prose-invert max-w-none \
[&>*:not(:last-child)]:mb-3.5 \
[&>ul]:list-disc [&>ol]:list-decimal [&>ul]:ml-4 [&>ol]:ml-4 \
[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-4 [&_ol]:ml-4 \
[&>h1]:text-2xl [&>h2]:text-xl [&>h3]:text-lg \
[&>h1]:font-bold [&>h2]:font-bold [&>h3]:font-bold \
[&>h1]:mt-6 [&>h2]:mt-5 [&>h3]:mt-4 \
[&>blockquote]:border-l-4 [&>blockquote]:border-neutral-300 [&>blockquote]:pl-4 [&>blockquote]:italic \
[&>pre]:bg-neutral-200/80 dark:[&>pre]:bg-muted/40 \
[&>pre]:p-4 \
[&>pre]:rounded-lg \
[&>pre]:my-4 \
[&>pre]:overflow-x-auto \
[&>:not(pre)>code]:bg-muted \
[&>:not(pre)>code]:text-primary \
[&>:not(pre)>code]:rounded \
[&>:not(pre)>code]:px-1.5 \
[&>:not(pre)>code]:py-0.5';

// Custom Mention extension
export const CustomMention = Mention.extend({
    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-user-id'),
                renderHTML: attributes => ({
                    'data-user-id': attributes.id,
                }),
            },
            label: {
                default: null,
                parseHTML: element => element.getAttribute('data-user-name'),
                renderHTML: attributes => ({
                    'data-user-name': attributes.label,
                }),
            },
            avatarColor: {
                default: 'bg-neutral-600',
                parseHTML: element => element.getAttribute('data-user-avatarColor'),
                renderHTML: attributes => ({
                    'data-user-avatarColor': attributes.avatarColor,
                }),
            },
        }
    },
});

// Shared mention rendering configuration
export const getMentionConfig = (axiosInstance, organizationSlug) => ({
    HTMLAttributes: {
        class: 'mention'
    },
    renderHTML({ node }) {
        const mentionClasses = AVATAR_MENTION_COLOR_MAP[node.attrs.avatarColor] || AVATAR_MENTION_COLOR_MAP['bg-neutral-600'];
        
        return [
            'span',
            {
                'class': `inline-flex items-center rounded-md ${mentionClasses} px-1.5 py-0.5 text-sm font-medium`,
                'data-type': 'mention',
                'data-user-id': node.attrs.id,
                'data-user-name': node.attrs.label,
                'data-user-avatarColor': node.attrs.avatarColor
            },
            [
                'span',
                {
                    'class': 'mention-name'
                },
                `@${node.attrs.label}`
            ]
        ];
    },
    renderText({ node }) {
        return `@${node.attrs.label}`;
    }
}); 